import { useEffect, useRef } from 'react'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { useNotificationsStore } from '@/features/notifications'
import {
  playNotificationSound,
  settingsService,
  shouldPlaySound,
  shouldShowNotification,
} from '@/features/settings'
import { useMeetingsStore } from '../store/meetings.store'
import { joinPathId } from '../utils/recurrence'

/**
 * Background scheduler that scans the meetings cache every TICK_MS and emits
 * a reminder notification when an upcoming meeting enters its reminder
 * window (`startTimeMs - reminderBeforeMinutes*60_000 <= now < startTimeMs`).
 *
 * Each `(meetingId, reminderBeforeMinutes)` pair fires at most once thanks
 * to the dedupe key stored on `metadata.reminderKey`.
 *
 * Mount this once at the root of the authenticated app (DashboardLayout) so
 * the user gets reminders regardless of which page they're on.
 */
const TICK_MS = 20_000 // 20s — granular enough for minute-level reminders.

export function useMeetingReminders() {
  const t = useTranslation()
  const meetings = useMeetingsStore((s) => s.meetings)
  const notifyReminderOnce = useNotificationsStore((s) => s.notifyReminderOnce)
  const meetingsRef = useRef(meetings)
  meetingsRef.current = meetings

  useEffect(() => {
    const tick = () => {
      const now = Date.now()
      // Read app settings *each tick* so flipping a toggle in the settings
      // page takes effect on the next 20s cycle — not at app boot.
      const appSettings = settingsService.get()
      const remindersAllowed = shouldShowNotification(
        appSettings.notifications,
        'reminder',
      )
      if (!remindersAllowed) return
      const remindersSoundAllowed = shouldPlaySound(
        appSettings.notifications,
        'reminder',
      )

      for (const meeting of meetingsRef.current) {
        const settings = meeting.settings
        if (!settings?.reminderEnabled) continue
        const lead = Number(settings.reminderBeforeMinutes) || 0
        if (lead <= 0) continue
        // Only fire for meetings that are still in the future.
        if (meeting.startTimeMs <= now) continue
        const windowOpensAt = meeting.startTimeMs - lead * 60_000
        if (now < windowOpensAt) continue

        const reminderKey = `${meeting.id}_${lead}`
        const minutesUntil = Math.max(1, Math.round((meeting.startTimeMs - now) / 60_000))

        const created = notifyReminderOnce(reminderKey, {
          type: 'reminder',
          title: t('notifications.meetingStartingSoon') || 'Meeting starting soon',
          message: `${meeting.title} • ${t('notifications.startsIn') || 'starts in'} ${minutesUntil} ${
            minutesUntil === 1
              ? t('notifications.minute') || 'minute'
              : t('notifications.minutes') || 'minutes'
          }`,
          actionUrl: `/meeting/${joinPathId(meeting)}`,
          metadata: {
            meetingId: meeting.id,
            reminderBeforeMinutes: lead,
          },
        })

        // Play the reminder sound only when (a) we actually created a fresh
        // notification (deduped reminders shouldn't ding twice) and (b) the
        // user has reminder sounds enabled.
        if (created && remindersSoundAllowed) {
          playNotificationSound('reminder')
        }
      }
    }

    tick()
    const intervalId = window.setInterval(tick, TICK_MS)
    return () => window.clearInterval(intervalId)
  }, [notifyReminderOnce, t])
}
